import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  ["everyone_eligible", "Everyone eligible"],
  ["in_sequence", "In the sequence now"],
  ["finished", "Finished the sequence"],
  ["pasted", "Pasted list"],
];
const MODES = [["spread", "Spread inside the daily cap"], ["fast", "As fast as the cap allows"]];

export default function AdminBroadcasts() {
  const [authed, setAuthed] = useState(null);
  const [pw, setPw] = useState("");
  const [csrf, setCsrf] = useState("");
  const [tab, setTab] = useState("new");

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("everyone_eligible");
  const [pasted, setPasted] = useState("");
  const [mode, setMode] = useState("spread");
  const [startAt, setStartAt] = useState("");
  const [aud, setAud] = useState(null);
  const [preview, setPreview] = useState(null);
  const [testedChecksum, setTestedChecksum] = useState("");
  const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);
  const [openRows, setOpenRows] = useState(null);

  const draft = () => ({ subject, body, audience_type: audience, delivery_mode: mode,
    pasted: audience === "pasted" ? pasted.split(/\r?\n/).map((s) => s.trim()).filter(Boolean) : null,
    start_at: startAt || null });
  const hdr = { headers: { "X-CSRF-Token": csrf }, withCredentials: true };

  const checkAuth = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/me`, { withCredentials: true }); setCsrf(r.data.csrf_token || ""); setAuthed(true); }
    catch { setAuthed(false); }
  }, []);
  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loadHistory = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/broadcasts`, { withCredentials: true }); setHistory(r.data.broadcasts); } catch {}
  }, []);
  useEffect(() => { if (authed && tab === "history") loadHistory(); }, [authed, tab, loadHistory]);

  const login = async (e) => {
    e.preventDefault();
    try { const r = await axios.post(`${API}/admin/login`, { password: pw }, { withCredentials: true }); setCsrf(r.data.csrf_token || ""); setAuthed(true); }
    catch { setMsg("Wrong password."); }
  };

  const renderPreview = async () => {
    const r = await axios.post(`${API}/admin/broadcasts/preview-render`, draft(), hdr);
    setPreview(r.data);
  };
  const computeAudience = async () => {
    const r = await axios.post(`${API}/admin/broadcasts/audience`, draft(), hdr);
    setAud(r.data);
  };
  useEffect(() => { if (authed) { renderPreview().catch(() => {}); } /* eslint-disable-next-line */ }, [subject, body, authed]);
  useEffect(() => { if (authed) { computeAudience().catch(() => {}); } /* eslint-disable-next-line */ }, [audience, mode, pasted, startAt, authed]);

  const currentChecksum = preview?.checksum || "";
  const testOk = testedChecksum && testedChecksum === currentChecksum;

  const sendTest = async () => {
    setMsg("Sending test…");
    try { const r = await axios.post(`${API}/admin/broadcasts/test`, draft(), hdr); setTestedChecksum(r.data.checksum); setMsg(`Test sent to ${r.data.sent_to}.`); }
    catch (e) { setMsg("Test failed: " + (e.response?.data?.detail || e.message)); }
  };

  const doStart = async () => {
    if (confirmText !== "SEND") return;
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/broadcasts/start?tested_checksum=${encodeURIComponent(testedChecksum)}`, draft(), hdr);
      setMsg(`Broadcast started (${r.data.count} recipients, ${r.data.status}).`);
      setConfirm(false); setConfirmText(""); setTab("history"); loadHistory();
    } catch (e) { setMsg("Start rejected: " + (e.response?.data?.detail || e.message)); }
    finally { setStarting(false); }
  };

  const action = async (bid, act) => {
    await axios.post(`${API}/admin/broadcasts/${bid}/action`, { action: act }, hdr);
    await loadHistory();
  };
  const viewRows = async (bid) => {
    const r = await axios.get(`${API}/admin/broadcasts/${bid}/recipients`, { withCredentials: true });
    setOpenRows({ bid, rows: r.data.recipients });
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center bg-paper text-muted">Loading…</div>;
  if (!authed) return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-6">
      <form onSubmit={login} className="w-full max-w-sm">
        <div className="label-mono text-muted">Broadcasts admin</div>
        <h1 className="font-display text-4xl mt-6 mb-8">Enter password.</h1>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full bg-transparent border-b border-ink py-3 outline-none" data-testid="bc-password" autoFocus />
        {msg && <p className="mt-3 text-sm text-red-600">{msg}</p>}
        <button className="mt-6 w-full bg-ink text-white py-3 label-mono" data-testid="bc-login">Enter</button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-broadcasts">
      <header className="border-b border-hairline sticky top-0 bg-white/90 backdrop-blur z-10">
        <div className="max-w-screen-lg mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-lg" style={{ fontWeight: 800 }}>Broadcasts</span>
          <div className="flex gap-2">
            {[["new", "New broadcast"], ["history", "History"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`label-mono px-3 py-1.5 border ${tab === k ? "bg-ink text-white border-ink" : "border-hairline"}`} data-testid={`bc-tab-${k}`}>{l}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-screen-lg mx-auto px-6 py-10">
        {msg && <p className="mb-5 text-sm" data-testid="bc-msg">{msg}</p>}

        {tab === "new" && (
          <div className="space-y-8">
            <div>
              <label className="label-mono text-muted">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-transparent border-b border-ink py-2 outline-none text-lg" data-testid="bc-subject" />
            </div>
            <div>
              <label className="label-mono text-muted">Body (plain text, blank line = new paragraph)</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="w-full bg-white border border-hairline p-3 outline-none text-sm" data-testid="bc-body" />
            </div>

            <div className="border border-hairline p-4">
              <div className="label-mono text-muted mb-2">Preview</div>
              <div className="bg-white border border-hairline p-3 max-h-72 overflow-auto text-sm" data-testid="bc-preview" dangerouslySetInnerHTML={{ __html: preview?.html || "" }} />
              <button onClick={sendTest} className="mt-3 label-mono border border-ink px-3 py-1.5 hover:bg-ink hover:text-white" data-testid="bc-send-test">Send test to me</button>
              {testOk && <span className="ml-3 label-mono text-emerald-700" data-testid="bc-test-ok">Test OK for current body ✓</span>}
            </div>

            <div>
              <div className="label-mono text-muted mb-2">Audience</div>
              <div className="flex flex-wrap gap-2">
                {AUDIENCES.map(([k, l]) => (
                  <button key={k} onClick={() => setAudience(k)} className={`label-mono px-3 py-1.5 border ${audience === k ? "bg-ink text-white border-ink" : "border-hairline"}`} data-testid={`bc-aud-${k}`}>{l}</button>
                ))}
              </div>
              {audience === "pasted" && <textarea value={pasted} onChange={(e) => setPasted(e.target.value)} rows={4} placeholder="one email per line" className="w-full mt-3 border border-hairline p-2 text-sm" data-testid="bc-pasted" />}
              {aud && (
                <div className="mt-3 text-sm" data-testid="bc-aud-summary">
                  <b>{aud.count}</b> recipients · excluded (unsub/bounced/complained): {aud.exclusions.unsubscribed_bounced_complained}
                  {aud.exclusions.not_in_bucket ? ` · not in bucket: ${aud.exclusions.not_in_bucket}` : ""}
                  {aud.unmatched?.length ? ` · unmatched (skipped): ${aud.unmatched.length}` : ""}
                  {" · projected finish: "}{aud.projected_finish || "—"}
                </div>
              )}
            </div>

            <div>
              <div className="label-mono text-muted mb-2">Delivery</div>
              <div className="flex flex-wrap gap-2">
                {MODES.map(([k, l]) => (
                  <button key={k} onClick={() => setMode(k)} className={`label-mono px-3 py-1.5 border ${mode === k ? "bg-ink text-white border-ink" : "border-hairline"}`} data-testid={`bc-mode-${k}`}>{l}</button>
                ))}
              </div>
              <label className="label-mono text-muted flex items-center gap-2 mt-3">Start (Europe/Berlin, blank = now)
                <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="border-b border-ink bg-transparent py-1" data-testid="bc-start-at" />
              </label>
            </div>

            <button disabled={!testOk || !aud?.count} onClick={() => setConfirm(true)} className="bg-ink text-white px-6 py-3 label-mono disabled:opacity-40 disabled:cursor-not-allowed" data-testid="bc-start">
              Start broadcast
            </button>
            {!testOk && <p className="text-xs text-muted mt-2">Start is disabled until a test send succeeds for the current body.</p>}
          </div>
        )}

        {tab === "history" && (
          <div className="space-y-4">
            {history.length === 0 && <p className="text-muted">No broadcasts yet.</p>}
            {history.map((b) => (
              <div key={b.id} className="border border-hairline p-4" data-testid={`bc-hist-${b.id}`}>
                <div className="flex justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-display text-lg">{b.subject}</div>
                    <div className="label-mono text-muted">{b.audience_type} · {b.audience_snapshot_count} · {b.delivery_mode} · <b>{b.status}</b></div>
                  </div>
                  <div className="flex gap-2 items-start">
                    {b.status === "paused_canary" && <button onClick={() => action(b.id, "continue")} className="label-mono border border-emerald-500 text-emerald-700 px-2 py-1" data-testid={`bc-continue-${b.id}`}>Continue</button>}
                    {["sending", "scheduled"].includes(b.status) && <button onClick={() => action(b.id, "pause")} className="label-mono border border-hairline px-2 py-1">Pause</button>}
                    {b.status === "paused" && <button onClick={() => action(b.id, "resume")} className="label-mono border border-hairline px-2 py-1">Resume</button>}
                    {!["done", "cancelled"].includes(b.status) && <button onClick={() => action(b.id, "cancel")} className="label-mono border border-red-300 text-red-600 px-2 py-1">Cancel</button>}
                    <button onClick={() => viewRows(b.id)} className="label-mono border border-hairline px-2 py-1" data-testid={`bc-rows-${b.id}`}>Rows</button>
                  </div>
                </div>
                <div className="mt-2 label-mono text-muted text-xs">
                  sent {b.counts.sent} · failed {b.counts.failed} · queued {b.counts.queued} · skipped {b.counts.skipped} ({Object.entries(b.skips_by_reason || {}).map(([k, v]) => `${k}:${v}`).join(", ") || "—"}) · unsubscribes {b.unsubscribes} · started {b.started_at || "—"} · finished {b.finished_at || "—"}
                </div>
                {openRows?.bid === b.id && (
                  <div className="mt-3 max-h-64 overflow-auto text-xs border-t border-hairline pt-2">
                    {openRows.rows.map((r, i) => (
                      <div key={i} className="flex justify-between border-b border-hairline/50 py-1">
                        <span>{r.email}</span><span className="text-muted">{r.status}{r.skip_reason ? ` (${r.skip_reason})` : ""} · {r.send_after?.slice(0, 16)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {confirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-20" data-testid="bc-confirm-modal">
          <div className="bg-white p-6 max-w-md w-full">
            <h3 className="font-display text-2xl mb-3">Confirm broadcast</h3>
            <ul className="text-sm space-y-1 mb-4">
              <li><b>Subject:</b> {subject}</li>
              <li><b>Audience:</b> {AUDIENCES.find((a) => a[0] === audience)?.[1]} ({aud?.count})</li>
              <li><b>Delivery:</b> {MODES.find((m) => m[0] === mode)?.[1]}</li>
              <li><b>Projected finish:</b> {aud?.projected_finish || "—"}</li>
            </ul>
            <p className="text-sm mb-2">Type <b>SEND</b> to confirm:</p>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full border-b border-ink py-2 outline-none mb-4" data-testid="bc-confirm-input" autoFocus />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setConfirm(false); setConfirmText(""); }} className="label-mono border border-hairline px-3 py-1.5">Cancel</button>
              <button disabled={confirmText !== "SEND" || starting} onClick={doStart} className="label-mono bg-ink text-white px-4 py-1.5 disabled:opacity-40" data-testid="bc-confirm-send">{starting ? "Starting…" : "Start"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
